import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import {
  type TicketDetail,
  type TicketSummary,
  ticketsApi,
} from "@/client/features/tickets/tickets-api";
import { ApiError } from "@/client/lib/api";
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABEL,
  TICKET_STATUS_LABEL,
  type TicketPriority,
  type TicketStatus,
} from "@/shared/ticket-status";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";
const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

const STATUS_VARIANT: Record<TicketStatus, "default" | "secondary" | "outline"> = {
  submitted: "secondary",
  in_progress: "default",
  resolved: "outline",
  closed: "outline",
};

function StatusBadge({ status }: { status: TicketStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{TICKET_STATUS_LABEL[status]}</Badge>;
}

const createSchema = z.object({
  title: z.string().trim().min(1, "Give your request a title.").max(200),
  description: z.string().trim().min(1, "Describe what you need.").max(5000),
  priority: z.enum(TICKET_PRIORITIES as unknown as [string, ...string[]]),
});

type CreateValues = z.infer<typeof createSchema>;

function NewRequestDialog({ onCreated }: { onCreated: (ticket: TicketSummary) => void }) {
  const [open, setOpen] = useState(false);
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { title: "", description: "", priority: "normal" },
  });

  const onSubmit = async (values: CreateValues) => {
    try {
      const ticket = await ticketsApi.create({
        title: values.title,
        description: values.description,
        priority: values.priority as TicketPriority,
      });
      toast.success(`Request ${ticket.identifier} submitted.`);
      form.reset({ title: "", description: "", priority: "normal" });
      setOpen(false);
      onCreated(ticket);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to submit the request. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>New request</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New support request</DialogTitle>
          <DialogDescription>
            Tell us what you need. Our team will follow up here, and you can track progress on this
            page.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Short summary of your request" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Details</FormLabel>
                  <FormControl>
                    <textarea
                      className={textareaClass}
                      placeholder="What's happening, what you expected, and anything that helps us help you."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="sm:max-w-xs">
                  <FormLabel>Priority</FormLabel>
                  <FormControl>
                    <select className={selectClass} {...field}>
                      {TICKET_PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {TICKET_PRIORITY_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Submitting…" : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function TicketsPage() {
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await ticketsApi.list();
      setTickets(res.tickets);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load your requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Support"
        description="Raise a request and track its progress. Our team responds right here."
      >
        <NewRequestDialog onCreated={() => void refresh()} />
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Your requests</CardTitle>
          <CardDescription>Requests you've opened with the Mantle team.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : tickets && tickets.length > 0 ? (
            <ul className="flex flex-col divide-y">
              {tickets.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/tickets/${t.id}`}
                    className="hover:bg-accent flex items-center justify-between gap-4 rounded-md px-2 py-3 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{t.title}</p>
                      <p className="text-muted-foreground text-xs">
                        {t.identifier} · Updated {new Date(t.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <StatusBadge status={t.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load your requests. Refresh to try again.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">
              No requests yet. Open one with “New request”.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReplyBox({ ticketId, onSent }: { ticketId: string; onSent: () => void }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const onSend = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      return;
    }
    setSending(true);
    try {
      await ticketsApi.reply(ticketId, trimmed);
      setBody("");
      toast.success("Reply sent.");
      // Refresh the thread separately: the reply is posted regardless of whether
      // the reload succeeds, so a failed refresh never looks like a failed reply.
      onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Unable to send your reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <textarea
        className={textareaClass}
        placeholder="Add a reply…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <Button className="w-fit" onClick={onSend} disabled={sending || body.trim().length === 0}>
        {sending ? "Sending…" : "Send reply"}
      </Button>
    </div>
  );
}

type DetailState = "loading" | "ready" | "notfound" | "error";

export function TicketDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [state, setState] = useState<DetailState>("loading");

  const load = useCallback(async () => {
    // Reset for the current id so a previous ticket/notfound never bleeds
    // through when navigating between detail URLs on the same mounted page.
    setState("loading");
    setTicket(null);
    try {
      setTicket(await ticketsApi.get(id));
      setState("ready");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState("notfound");
      } else {
        setState("error");
        toast.error(err instanceof ApiError ? err.message : "Failed to load this request.");
      }
    }
  }, [id]);

  // Re-read the thread in place (no skeleton flash) after posting a reply. A
  // failure here is non-fatal: the reply already landed, so just keep the view.
  const refresh = useCallback(async () => {
    try {
      setTicket(await ticketsApi.get(id));
    } catch {
      toast.error("Couldn't refresh the thread. Reload to see the latest.");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state === "notfound") {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" className="w-fit" onClick={() => navigate("/tickets")}>
          <ArrowLeft /> Back to requests
        </Button>
        <p className="text-muted-foreground text-sm">This request doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Button variant="ghost" className="w-fit" onClick={() => navigate("/tickets")}>
        <ArrowLeft /> Back to requests
      </Button>

      {state === "error" ? (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">
            Couldn't load this request. Refresh to try again.
          </p>
          <Button variant="outline" className="w-fit" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : state === "loading" || !ticket ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">{ticket.title}</h1>
              <StatusBadge status={ticket.status} />
            </div>
            <p className="text-muted-foreground text-xs">
              {ticket.identifier} · Opened {new Date(ticket.createdAt).toLocaleDateString()}
            </p>
          </div>

          {ticket.description ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Conversation</CardTitle>
              <CardDescription>Replies from you and the Mantle team.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {ticket.comments.length === 0 ? (
                <p className="text-muted-foreground text-sm">No replies yet.</p>
              ) : (
                <ul className="flex flex-col gap-4">
                  {ticket.comments.map((c) => (
                    <li key={c.id} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{c.author}</span>
                        <span className="text-muted-foreground text-xs">
                          {new Date(c.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{c.body}</p>
                    </li>
                  ))}
                </ul>
              )}
              <ReplyBox ticketId={ticket.id} onSent={() => void refresh()} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
